import { createContext, useContext } from 'react';

export type TabsVariant = 'primary' | 'secondary';

export type TabsSize = 'responsive' | 'mobile' | 'desktop';

export type TabsContextValue = {
  /**
   * Enable horizontal auto-scroll when there are many tabs.
   * When enabled, the tabs list will automatically scroll to keep the active tab visible.
   * @default false
   */
  withHorizontalAutoScroll?: boolean;
  /**
   * Enable layout animations for the active indicator using Motion.
   * @default true
   */
  animated?: boolean;
  /**
   * Layout ID for Motion layout animations.
   * No need to set it in most cases as it will be internally generated by useId() (stable id).
   */
  layoutId?: string;
  /**
   * The current active tab value
   */
  activeValue?: string;
  /**
   * Callback when the active tab value changes
   */
  onActiveValueChange?: (activeValue: string) => void;
  /**
   * The size of the tabs. It's responsive by default.
   * If needed you can force it by setting it to 'mobile' or 'desktop'.
   * Secondary variant has a single size and should not set this prop.
   */
  size?: TabsSize;
  /**
   * Visual variant of the tabs.
   */
  variant?: TabsVariant;
};

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

export const TabsProvider = TabsContext.Provider;

export const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabsContext must be used within a TabsProvider');
  }
  return context;
};
